import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Markdown, ArrowLink } from "components";
import { useAnchor } from "hooks";
import { Title2Content } from "types/graphql/generated";

interface Props {
  content: Title2Content;
}

const Title2: VFC<Props> = withErrorBoundary(({ content: { title, description, title2Link, title2LinkLabel } }) => {
  useAnchor("title2");

  return (
    <StyledTitle2 className="title2 Content">
      <div className="Title2__container">
        <h2 className="Title2__title Font_48_54_600 md:Font_40_48_600 sm:Font_32_40_600 Color_primary">{title}</h2>
        <div className="Title2__text">
          <Markdown className="Font_24_32_450 sm:Font_18_26_450 Color_secondary">{description}</Markdown>
          {title2Link && title2LinkLabel && (
            <ArrowLink href={title2Link} className="Title2__arrowLink Font_18_26_600 Color_primary">
              {title2LinkLabel}
            </ArrowLink>
          )}
        </div>
      </div>
    </StyledTitle2>
  );
}, {});

const StyledTitle2 = styled.section`
  margin-top: -64px;
  padding-top: 64px;

  .Title2__container {
    display: flex;
    justify-content: space-between;
  }

  .Title2__title {
    max-width: 544px;
    min-width: 490px;
  }

  .Title2__text {
    margin-left: 42px;
    max-width: 604px;
  }

  .Title2__arrowLink {
    margin-top: 24px;

    svg {
      width: 12px;
    }

    :hover {
      opacity: 0.8;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Title2__container {
      flex-direction: column;
    }

    .Title2__title {
      min-width: unset;
      max-width: unset;
    }

    .Title2__text {
      margin-top: 32px;
      margin-left: unset;
      max-width: unset;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .Title2__text {
      margin-top: 16px;
    }
  }
`;

gql`
  fragment Title2Content on ComponentTitleTitle2 {
    title
    description
    title2LinkLabel
    title2Link
  }
`;

export { Title2 };
export type { Props as Title2Props };
